import { useCallback, useMemo } from 'react';
import { User } from 'lucide-react';
import { EducateNavHeader } from '@/components/brand/EducateNavHeader';
import { AudioTransport } from '@/components/player/AudioTransport';
import { ErrorScreen } from '@/components/StatusScreens';
import { useAudioPlayback, type AudioClip } from '@/hooks/useAudioPlayback';
import type { EducateModule } from '@/hooks/useEducateModule';
import type { AudiobookContent, TextUnit } from '@/types/educate';

const BODY_GUTTER = 'px-[15%]';

function buildSlideClips(content: AudiobookContent): AudioClip[] {
  const slides = Array.isArray(content.slides) ? content.slides : [];
  const units = Array.isArray(content.textUnits) ? content.textUnits : [];
  const unitsBySequence = new Map<number, TextUnit>();
  for (const unit of units) unitsBySequence.set(unit.sequenceNo, unit);

  return slides.map((slide, index) => {
    const unit = unitsBySequence.get(index) ?? units[index];
    return {
      audioUrl: unit?.audioUrl ?? slide.audioUrl ?? null,
      text: unit?.textContent ?? slide.script ?? ''
    };
  });
}

export function AudiobookPlayer({ module }: { module: EducateModule }) {
  const { storyline, units, setCurrentPosition, finishModule, interactions, currentIndex } = module;
  const content = storyline?.content as AudiobookContent | undefined;
  const showLogo = storyline?.uiContent.uiConfig?.showKnolskapeLogo ?? true;

  const clips = useMemo(() => (content ? buildSlideClips(content) : []), [content]);

  const narratorVoice = content?.voiceAssignments?.narrator;
  const narratorName = narratorVoice?.voiceName ?? 'Adam';

  const markSlideHeard = useCallback(
    (slideIndex: number) => {
      const unitId = units[slideIndex]?.id;
      if (!unitId) return;
      setCurrentPosition(slideIndex, unitId);
    },
    [setCurrentPosition, units]
  );

  const onAllEnded = useCallback(() => {
    const allUnitIds = units.map((unit) => unit.id);
    finishModule(allUnitIds, interactions);
  }, [finishModule, interactions, units]);

  const playback = useAudioPlayback({
    clips,
    initialIndex: currentIndex,
    onIndexChange: markSlideHeard
  });

  const slides = Array.isArray(content?.slides) ? content!.slides : [];
  const total = slides.length;
  const currentSlide = slides[playback.currentIndex];

  if (!content || total === 0) {
    return (
      <EducateNavHeader
        eyebrow="Micro Audiobook"
        title={storyline?.uiContent.title ?? '4E Educate'}
        showLogo={showLogo}
      >
        <main className="w-full px-[15%] py-12">
          <ErrorScreen message="This audiobook has no slides to play." />
        </main>
      </EducateNavHeader>
    );
  }

  const nextSlide = slides[playback.currentIndex + 1];
  const isFirstSlide = playback.currentIndex <= 0;
  const isLastSlide = playback.currentIndex >= total - 1;

  return (
    <EducateNavHeader
      eyebrow="Micro Audiobook"
      title={storyline?.uiContent.title ?? '4E Educate'}
      showLogo={showLogo}
    >
      <main className={`flex min-h-0 w-full flex-1 flex-col overflow-hidden ${BODY_GUTTER} pt-4`}>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-brand-600" />
            <span className="font-semibold text-slate-700">Narrator</span>
            <span className="text-slate-500">— {narratorName}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-500">
              {playback.currentIndex + 1} / {total}
            </span>
            <div className="flex items-center gap-1.5">
              {slides.map((slide, index) => (
                <button
                  key={`${slide.title}-${index}`}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => playback.seekTo(index)}
                  className={
                    index === playback.currentIndex
                      ? 'h-2 w-6 rounded-full bg-brand-600 transition-all'
                      : 'h-2 w-2 rounded-full bg-slate-300 transition-all hover:bg-slate-400'
                  }
                />
              ))}
            </div>
          </div>
        </div>

        <div
          className="relative my-5 flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
          data-testid="audiobook-screen"
        >
          <div className="flex h-full flex-col overflow-y-auto px-10 pt-10 pb-44 lg:px-14 lg:pt-14">
            <h2 className="font-serif text-3xl font-bold leading-tight text-slate-900 lg:text-4xl">
              {currentSlide?.title}
            </h2>

            <ul className="mt-8 flex-1 space-y-5">
              {(currentSlide?.bullets ?? []).map((bullet, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="mt-2.5 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400" />
                  <span className="text-xl leading-relaxed text-slate-700 lg:text-2xl">
                    {bullet}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <NarratorAvatar isPlaying={playback.isPlaying} />
        </div>
      </main>

      <footer className="shrink-0 overflow-hidden border-t border-slate-200 bg-white">
        <div className={`${BODY_GUTTER} py-4`}>
          <AudioTransport
            variant="slides"
            isPlaying={playback.isPlaying}
            progress={playback.progress}
            onToggle={playback.toggle}
            onPrev={playback.prev}
            onNext={playback.next}
            prevDisabled={isFirstSlide}
            nextDisabled={isLastSlide}
            onFinish={onAllEnded}
            isLast={isLastSlide}
            finishEnabled={playback.lastClipEnded}
            leftCaption={
              <span className="truncate font-semibold text-slate-700">{currentSlide?.title}</span>
            }
            rightCaption={
              nextSlide ? (
                <span className="truncate italic text-slate-400">{nextSlide.script}</span>
              ) : null
            }
          />
        </div>
      </footer>
    </EducateNavHeader>
  );
}

const WAVEFORM_PEAKS = [0.35, 0.6, 0.85, 1, 0.7, 0.95, 0.55, 0.8, 0.45, 0.65, 0.3];
const WAVEFORM_MAX_HEIGHT_PX = 18;

function NarratorAvatar({ isPlaying }: { isPlaying: boolean }) {
  return (
    <div className="pointer-events-none absolute bottom-6 right-6 flex h-32 w-32 items-center justify-center">
      {isPlaying &&
        [0, 1, 2, 3].map((ringIndex) => (
          <span
            key={ringIndex}
            className="absolute h-[84px] w-[84px] rounded-full border-2 border-brand-400/60"
            style={{
              animation: 'narratorRing 2.4s ease-out infinite',
              animationDelay: `${ringIndex * 0.6}s`
            }}
          />
        ))}

      <div className="relative flex h-[84px] w-[84px] flex-col items-center justify-center rounded-full bg-gradient-to-b from-slate-700 to-slate-900 text-white shadow-lg">
        <User size={34} />
        <span className=" flex h-5 items-center gap-[2px]" aria-hidden="true">
          {WAVEFORM_PEAKS.map((peak, barIndex) => (
            <span
              key={barIndex}
              className="w-[2px] rounded-full bg-white/85"
              style={{
                height: `${Math.max(2, Math.round(peak * WAVEFORM_MAX_HEIGHT_PX))}px`,
                transformOrigin: 'center',
                animation: isPlaying
                  ? `narratorWave ${0.7 + (barIndex % 4) * 0.12}s ease-in-out ${barIndex * 0.07}s infinite`
                  : 'none',
                transform: isPlaying ? undefined : 'scaleY(0.3)'
              }}
            />
          ))}
        </span>
      </div>

      <style>{`
        @keyframes narratorRing { 0% { transform: scale(0.55); opacity: 0.8; } 100% { transform: scale(1.7); opacity: 0; } }
        @keyframes narratorWave { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
      `}</style>
    </div>
  );
}
